import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Wand2, BarChart3, BookOpen, Search, Mic, Heart, Compass,
  Sparkles, Shield, ShieldCheck, History, LogIn, KeyRound, Footprints, Mic2,
  Users, Brain, MapPin, HandHeart, Check, FileText, Upload, Briefcase, Trophy,
  Target, Lightbulb, ChevronDown, ChevronUp, LayoutDashboard,
} from "lucide-react";
import NewsTicker from "../components/NewsTicker";
import WelcomeIntro from "../components/WelcomeIntro";
import { useBetaGate } from "../context/BetaGateContext";
import { useAuth } from "../context/AuthContext";
import ReportsMenu from "../components/ReportsMenu";
import { PHASE_GRADIENTS } from "../components/dashboard/LeftCards";
import ResmoAuthenticitySeal from "../components/ResmoAuthenticitySeal";
import WelcomeTour from "../components/WelcomeTour";
import { logActivity } from "../lib/activity";

/* Small wrapper so the Welcome page can show the seal at "fully reviewed"
   state. We pass the canonical fixture share token so the rendered seal is a
   real SVG from the backend — same single source of truth as the live seal. */
const WELCOME_SEAL_FIXTURE_TOKEN = "9894b5146037415b8234e7d7c6cbdb5f";

function WelcomeSealPreview() {
  return (
    <ResmoAuthenticitySeal
      tokenOverride={WELCOME_SEAL_FIXTURE_TOKEN}
      verified={{
        flag_count: 6,
        pending_count: 0,
        all_reviewed: true,
        summary: "All 6 claims in this tailored resume are anchored to the candidate's original base resume.",
      }}
    />
  );
}

/**
 * The 5 phases shown in the Welcome page toolkit — mirrors the dashboard
 * PhaseProgressBar so first-time visitors recognize the same circle pattern
 * once they sign in. Each phase carries 2-3 sub-steps (the tools that live
 * inside it).
 */
const WELCOME_PHASES = [
  {
    id: 1,
    label: "Resume",
    tagline: "Know yourself first",
    Icon: FileText,
    stat: "60% of interview decisions are made in week one — move fast.",
    source: "LinkedIn Research — Nearly 80% of people feel unprepared to find a job in 2026 (Jan 7, 2026)",
    subSteps: [
      { Icon: Wand2,     label: "Create / Upload Resume", route: "/feed-resume",       sub: "Build one with Resmo's 6-step guided builder, or drop a PDF / DOCX / TXT — your base unlocks every other tool." },
      { Icon: BarChart3, label: "Skill Assessment",       route: "/skills",            sub: "Salary range, adjacent roles, and your business currency — anchored to your real skills." },
      { Icon: Compass,   label: "Role Research",          route: "/skills/role/new",   sub: "Day-in-the-life, comp tiers, career ladders, and how to break in — perfect for industry switchers." },
      { Icon: Search,    label: "Job Search",             route: "/job-search",        sub: "Real Adzuna postings — one-click tailor your resume to any that catches your eye." },
    ],
  },
  {
    id: 2,
    label: "Tailor",
    tagline: "Match the role's real problem",
    Icon: Sparkles,
    stat: "5.8% of tailored resumes land interviews · vs 3.7% untailored.",
    source: "Sam Wright — Job Search Tips for 2026 (May 14, 2026)",
    subSteps: [
      { Icon: BookOpen,    label: "Upload Job Description",    route: "/feed-job",       sub: "Paste any job description or pull one from live Adzuna search — Resmo extracts the role's real problem and tone." },
      { Icon: Sparkles,    label: "Role Strategy Brief",       route: "/strategy-brief", sub: "The why behind the role + your skills vs the JD's requirements — a one-page coach's briefing in 60 seconds." },
      { Icon: Target,      label: "ATS Assessment & Clean-up", route: "/studio",         sub: "Resmo scores your tailored resume against the JD (0-100). Iterate with one-click clean-up suggestions until you hit 60+." },
      { Icon: ShieldCheck, label: "Honesty Check",             route: "/studio",         sub: "Resmo audits every tailored claim against your original — flags any expansion or fabrication for you to approve." },
    ],
  },
  {
    id: 3,
    label: "Network",
    tagline: "Show up where decisions are made",
    Icon: Users,
    stat: "1 in 10 hires comes from a warm referral — work the room.",
    source: "Roy Maurer — Majority of Employee Referrals Made During Work Hours, SHRM (Feb 25, 2025)",
    subSteps: [
      { Icon: Mic2,     label: "Elevator Pitch Studio", route: "/network?t=pitch",  sub: "Resmo drafts a 30-second pitch from your resume + target role. Edit it, rehearse it, land in the room with confidence." },
      { Icon: Users,    label: "Career Crew",           route: "/network?t=people", sub: "The people in your corner — every contact you've met, what they care about, last touch, and the warm intros they could earn you." },
      { Icon: Sparkles, label: "Conversation Sparkers", route: "/network?t=sparks", sub: "AI-drafted openers, reciprocity ideas, and follow-up nudges tuned to each contact — so you never stare at a blank message box." },
      { Icon: MapPin,   label: "Communities & Events",  route: "/network?t=events", sub: "Curated communities and live events Resmo has matched to your target role — where the right people actually hang out." },
    ],
  },
  {
    id: 4,
    label: "Interview",
    tagline: "Walk in two steps ahead",
    Icon: Mic,
    subSteps: [
      { Icon: Trophy,    label: "Interview Playbook", route: "/playbooks",            sub: "Likely questions + STAR-framed answers anchored to your actual experience — your one-page cheat sheet." },
      { Icon: Briefcase, label: "Company Research",   route: "/strategy-brief",       sub: "Role + company positioning: their real problem, your business currency, ATS gaps — so you speak their language." },
      { Icon: Search,    label: "Interviewer Prep",   route: "/interviewer-research", sub: "Paste their LinkedIn URL — Resmo (via Perplexity Sonar Pro) returns a briefing, talking points, likely questions, dos & don'ts." },
      { Icon: Mic2,      label: "Dress Rehearsal",    route: "/mock-interview",       sub: "Voice or text mock with Resmo — every answer scored, then bundled into a calm 5-minute audio briefing you can listen to on the way in." },
    ],
  },
  {
    id: 5,
    label: "Stepping Stones",
    tagline: "Build forward from every interview",
    Icon: Heart,
    subSteps: [
      { Icon: Heart,     label: "Lessons Learned", route: "/stepping-stones", sub: "30-second debrief after each interview — what worked, what to focus on. Resmo auto-creates the journal entry." },
      { Icon: BarChart3, label: "Track Progress",  route: "/history",         sub: "Live outcome tracker: interviews logged, offer rate, next-round rate. Patterns surface in your next role." },
      { Icon: Lightbulb, label: "Tips & Tools",    route: "/resources",       sub: "Resmo's playbook of mindset cues, salary scripts, and proven follow-up templates — pulled from what's actually working for other job-seekers." },
      { Icon: History,   label: "History",         route: "/history",         sub: "Every tailored resume, cover letter, and interview outcome — one timeline so you can see what's moving the needle." },
    ],
  },
];

/**
 * PillarBlock — a vertical "Eat / Digest / Impress" label that sits on the
 * left of the Five-Phases grid. Pure gradient text (no background box) so
 * the word floats on the white surface. Reads bottom-to-top like a book spine.
 *
 * Flex proportions: Eat=1, Digest=1, Impress=3 → aligns with 5 phase cards.
 * `align` controls where the label sits in its container — "center" (default),
 * "start" (top of container), or a numeric percent like 0.16 (the Impress
 * case: position so the label centers vertically on the FIRST card of its
 * 3-card span = the Network/Phase 3 card).
 */
function PillarBlock({ label, textGradient, fallbackColor, flex, testid, align = "center" }) {
  // Resolve alignment style. Numeric values position the label as a percentage
  // of the container height (0 = top, 1 = bottom). Used to land "Impress" at
  // the middle of the Network card inside its 3-card flex=3 span.
  //
  // IMPORTANT: when numeric align is set, the inner-wrapper transform must
  // PRESERVE the `rotate(180deg)` that makes the label read bottom-to-top
  // (matching Eat + Digest). Combining the translate + rotate in a single
  // `transform` value, otherwise the rotate gets silently overridden and the
  // word reads in the wrong direction.
  const isNumericAlign = typeof align === "number";
  const alignClass = isNumericAlign
    ? "justify-center"
    : `justify-center ${align === "start" ? "items-start" : "items-center"}`;
  const innerWrapperStyle = isNumericAlign
    ? {
        position: "absolute",
        top: `${align * 100}%`,
        left: "50%",
        transform: "translate(-50%, -50%) rotate(180deg)",
      }
    : undefined;

  return (
    <div
      className={`relative flex ${alignClass}`}
      style={{
        flex: `${flex} 1 0`,
        minHeight: 120,
        overflow: "visible",
      }}
      data-testid={testid}
    >
      <span
        className="rm-cursive font-bold select-none"
        style={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontSize: "clamp(3.5rem, 7.5vh, 6rem)",
          lineHeight: 1.15,
          background: textGradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          color: fallbackColor,
          letterSpacing: "-0.02em",
          padding: "0.5rem 0.6rem",
          paddingBottom: "1.1rem",
          overflow: "visible",
          ...(innerWrapperStyle || {}),
        }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * QuickStartPicker — "Where do you want to start?" door picker shown right
 * under the hero CTAs. Eight large clickable cards, each phrased in the
 * tester's own voice ("I want to…") so the next step is obvious. Cards
 * navigate straight to the target tool; ProtectedRoute handles the auth
 * detour for unauthenticated users.
 *
 * Visual: 4-col on desktop, 2-col on tablet, 1-col on mobile. Each card uses
 * the brand burnt-orange icon chip on the left + navy title + gray subtitle.
 * Hover lift uses the existing rm-card pattern from the rest of the app.
 */
const QUICK_START_DOORS = [
  {
    id: "dashboard",
    intent: "I want to explore my dashboard",
    title: "My Dashboard",
    sub: "Your home base — see everything Resmo's built for you and jump into any tool from one place.",
    Icon: LayoutDashboard,
    route: "/",
    tint: "#0F1B2D",
  },
  {
    id: "makeover",
    intent: "I want a resume makeover",
    title: "Resume Makeover",
    sub: "Upload your current resume and let Resmo digest it into your base story.",
    Icon: Wand2,
    route: "/feed-resume",
    tint: "#FF6A00",
  },
  {
    id: "fit",
    intent: "I want to see if a job fits my skills",
    title: "Fit a Job to Me",
    sub: "Paste any JD — get a Fit Score, the role's real problem, and a tailored resume in 30 seconds.",
    Icon: Target,
    route: "/feed-job",
    tint: "#B91C1C",
  },
  {
    id: "salary",
    intent: "I want to see what my skills are worth",
    title: "Salary & Skills",
    sub: "Rate your skills, pick a target role, get a salary range + adjacent roles your skills also fit.",
    Icon: BarChart3,
    route: "/skills/new",
    tint: "#0E5A4A",
  },
  {
    id: "practice",
    intent: "I want to practice my interview",
    title: "Mock Interview",
    sub: "Live coach-rubric practice room. Type or record your answer; get a 4-point score + a 'stronger answer' in your voice.",
    Icon: Mic,
    route: "/mock-interview",
    tint: "#7C3AED",
  },
  {
    id: "interviewer",
    intent: "I want to learn about my interviewer",
    title: "Interviewer Research",
    sub: "Drop the interviewer's name + company. Resmo profiles their background, focus areas, and likely questions.",
    Icon: Brain,
    route: "/interviewer-research",
    tint: "#0F4C81",
  },
  {
    id: "role",
    intent: "I want to research a new role",
    title: "Role Research",
    sub: "Salary tiers, must-haves, career path for any role — even ones you've never held.",
    Icon: Compass,
    route: "/skills/role/new",
    tint: "#D97706",
  },
  {
    id: "jobs",
    intent: "I want to browse live jobs",
    title: "Live Job Search",
    sub: "Live Adzuna listings with one-click tailor — and curated search links across LinkedIn, Indeed, Glassdoor, Wellfound, more.",
    Icon: Search,
    route: "/job-search",
    tint: "#0891B2",
  },
  {
    id: "network",
    intent: "I want to build my career network",
    title: "Career Crew",
    sub: "Your contacts journal — every person in your corner, last touch, and warm-intro ideas Resmo can draft for you.",
    Icon: Users,
    route: "/network",
    tint: "#BE185D",
  },
];

function QuickStartPicker({ isAuthenticated, gateUnlocked }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Smart default — recommend a contextual "next step" door based on what
  // the tester has done so far. Four states, in order of progression:
  //   0. First-time visitor (no resume, no analyses) → "My Dashboard"
  //      (introduce them to the home base; everything branches from here)
  //   1. Has resume, no analyses    → "Fit a Job to Me" (drop a JD)
  //   2. Has resume + analyses      → "Mock Interview"  (sharpen for room)
  //   3. Has analyses but no resume → "Resume Makeover" (rare edge — fallback)
  // Unauthenticated visitors are also routed to "My Dashboard" since the
  // dashboard is the natural first landing after they unlock the gate +
  // sign up — they get a "head straight home" recommendation, the auth
  // detour wires them through gate → register → dashboard.
  const recommendedDoorId = (() => {
    if (!isAuthenticated || !user) return "dashboard";
    const hasResume = !!user.has_base_resume;
    const hasAnalyses = (user.analyses_count || 0) > 0;
    if (!hasResume && !hasAnalyses) return "dashboard";
    if (hasResume && !hasAnalyses) return "fit";
    if (hasResume && hasAnalyses) return "practice";
    return "makeover";
  })();
  const recommendedDoor =
    QUICK_START_DOORS.find((d) => d.id === recommendedDoorId) ||
    QUICK_START_DOORS[0];
  // For unauthenticated users we still want the door to feel useful — clicking
  // a card stashes the intended destination in sessionStorage and routes the
  // user to /gate (if not unlocked) or /register (if unlocked but not logged
  // in). Login.jsx + Register.jsx will check this stash on success and warp
  // the user to the right tool right after auth — no second click needed.
  const onPick = (door) => {
    // Fire analytics first so we capture the intent. Two important nuances:
    //  1. PostHog's `capture()` is in-memory + batched, so it survives SPA
    //     navigation. ✓
    //  2. The backend `/api/activity/log` POST, however, races with
    //     `navigate(...)` — React Router's synchronous state change can
    //     unmount the component and the browser may cancel the pending XHR.
    //     We defer the navigate by one event-loop tick so the request has
    //     time to leave the browser. 0ms is enough — we just need to yield.
    try {
      logActivity("quickstart_door_picked", {
        door_id: door.id,
        door_title: door.title,
        door_intent: door.intent,
        destination_route: door.route,
        authenticated: isAuthenticated,
        gate_unlocked: gateUnlocked,
      });
    } catch { /* analytics never block routing */ }

    const doNavigate = () => {
      if (isAuthenticated) {
        navigate(door.route);
        return;
      }
      try {
        sessionStorage.setItem("resmo:postAuthNext", door.route);
        sessionStorage.setItem("resmo:postAuthIntent", door.intent);
      } catch { /* sessionStorage blocked */ }
      if (!gateUnlocked) {
        navigate("/gate");
      } else {
        navigate("/register");
      }
    };
    // Yield so the analytics POST flushes before navigation tears down.
    setTimeout(doNavigate, 0);
  };

  // Compact dropdown UX — keep the page calm: doors are hidden behind a
  // single CTA, revealed only when the tester decides to engage. State is
  // open/closed; clicking outside or picking an option closes it. Escape key
  // also closes it for keyboard users.
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handlePick = (door) => {
    setOpen(false);
    onPick(door);
  };

  return (
    <div
      className="rm-card-static p-5 md:p-6 relative w-full h-full flex flex-col"
      style={{
        background: "white",
        borderColor: "#0F1B2D",
        boxShadow: "5px 5px 0 0 #0F1B2D",
      }}
      data-testid="welcome-quickstart-picker"
    >
      <div className="font-mono-rm uppercase text-[11px] tracking-widest mb-2 rm-text-orange"
           style={{ fontWeight: 700 }}
           data-testid="quickstart-eyebrow">
        · Guided start
      </div>
      <h2
        className="font-display leading-tight mb-2"
        style={{
          fontSize: "clamp(1.4rem, 2.6vw, 2rem)",
          color: "#0F1B2D",
          letterSpacing: "-0.02em",
        }}
        data-testid="quickstart-headline"
      >
        Where do you want to{" "}
        <span
          style={{
            background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            color: "#B91C1C",
          }}
        >
          start
        </span>
        ?
      </h2>
      <p className="text-xs md:text-sm leading-relaxed mb-4"
         style={{ color: "#475569" }}>
        Pick the door that fits today. Resmo handles the rest.
      </p>

      {/* Recommended for you — personalized next step. One-click warp that
          fills the empty middle of the card AND surfaces a smart default so
          the tester doesn't have to think. Switches as the tester progresses:
          no resume → Resume Makeover; resume + no analyses → Fit a Job;
          both → Mock Interview. */}
      <button
        type="button"
        onClick={() => onPick(recommendedDoor)}
        className="group w-full text-left rm-card-static p-3 md:p-4 mb-3 transition-transform hover:-translate-y-0.5 focus:outline-none"
        style={{
          background:
            "linear-gradient(135deg, #FFFBEB 0%, #FFF7ED 100%)",
          borderColor: recommendedDoor.tint,
          borderStyle: "dashed",
          borderWidth: "2px",
          boxShadow: "none",
        }}
        data-testid="quickstart-recommended"
        aria-label={`Recommended next step: ${recommendedDoor.title}`}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            {/* Pulsing ring — pace-matched to the video's poster animation. */}
            <span
              aria-hidden
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                background: recommendedDoor.tint,
                opacity: 0.35,
                animationDuration: "1.8s",
              }}
            />
            <div
              className="relative w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: recommendedDoor.tint,
                color: "white",
                border: "3px solid #0F1B2D",
              }}
              aria-hidden
            >
              <recommendedDoor.Icon size={17} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="font-mono-rm uppercase text-[9px] tracking-widest leading-snug flex items-center gap-1.5"
              style={{ color: recommendedDoor.tint, fontWeight: 700 }}
            >
              <Sparkles size={10} /> Recommended for you
            </div>
            <div
              className="font-display uppercase leading-tight mt-0.5"
              style={{
                fontSize: "1rem",
                color: "#0F1B2D",
                letterSpacing: "-0.01em",
              }}
            >
              {recommendedDoor.title}
            </div>
            <div
              className="text-[11px] mt-0.5 line-clamp-2"
              style={{ color: "#64748B" }}
            >
              {recommendedDoor.sub}
            </div>
          </div>
          <ArrowRight
            size={15}
            className="flex-shrink-0 transition-transform group-hover:translate-x-1"
            style={{ color: recommendedDoor.tint }}
          />
        </div>
      </button>

      <div className="font-mono-rm uppercase text-[9px] tracking-widest mb-2 text-center"
           style={{ color: "#94A3B8", fontWeight: 700 }}>
        — or —
      </div>

      {/* Flex-spacer area between the OR divider and the dropdown CTA — fills
          whatever vertical space the picker has beyond its natural content
          height so the card balances visually with the video. We slot in a
          small Resmo mascot peek + a "8 doors waiting" stat as gentle teasers
          that earn the pixels without adding interaction noise. The dropdown
          CTA still anchors to the bottom via `mt-auto`. */}
      <div className="flex-1 flex flex-col items-center justify-center py-3 min-h-0">
        <img
          src="/brand/resmo-welcoming.png"
          alt=""
          className="h-16 md:h-20 w-auto opacity-90 select-none pointer-events-none"
          style={{ filter: "drop-shadow(0 6px 14px rgba(15,27,45,0.18))" }}
          data-testid="quickstart-mascot-peek"
        />
        <div
          className="font-mono-rm uppercase text-[10px] tracking-widest text-center mt-2"
          style={{ color: "#64748B", fontWeight: 700 }}
        >
          <span style={{ color: "#FF6A00", fontWeight: 800 }}>{QUICK_START_DOORS.length}</span>{" "}
          doors waiting
        </div>
      </div>

      <div ref={panelRef} className="relative mt-auto">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
          className="w-full flex items-center justify-between gap-3 rm-btn rm-btn-primary py-3 px-4 text-sm"
          style={{ boxShadow: "4px 4px 0 0 #0F1B2D" }}
          data-testid="quickstart-toggle"
        >
          <span className="font-display uppercase tracking-tight text-left flex-1">
            Choose your starting point
          </span>
          <ChevronDown
            size={16}
            className="flex-shrink-0 transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>

        {open && (
          <div
            role="listbox"
            aria-label="Choose your starting point"
            className="absolute left-0 right-0 mt-2 rm-card-static p-2 max-h-[420px] overflow-y-auto z-20"
            style={{
              background: "white",
              borderColor: "#0F1B2D",
              boxShadow: "6px 6px 0 0 #0F1B2D",
            }}
            data-testid="quickstart-menu"
          >
            {QUICK_START_DOORS.map((door) => {
              const Ico = door.Icon;
              return (
                <button
                  key={door.id}
                  type="button"
                  role="option"
                  onClick={() => handlePick(door)}
                  className="group w-full text-left flex items-start gap-3 p-3 rounded-md transition-colors hover:bg-[#FFF7ED] focus:outline-none focus:bg-[#FFF7ED]"
                  data-testid={`quickstart-door-${door.id}`}
                  aria-label={door.intent}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: door.tint,
                      color: "white",
                      border: "2px solid #0F1B2D",
                    }}
                    aria-hidden
                  >
                    <Ico size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-mono-rm uppercase text-[9px] tracking-widest leading-snug"
                      style={{ color: door.tint, fontWeight: 700 }}
                    >
                      {door.intent}
                    </div>
                    <div
                      className="font-display uppercase leading-tight mt-0.5"
                      style={{
                        fontSize: "0.95rem",
                        color: "#0F1B2D",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {door.title}
                    </div>
                    <div
                      className="text-[11px] mt-0.5 line-clamp-2"
                      style={{ color: "#64748B" }}
                    >
                      {door.sub}
                    </div>
                  </div>
                  <ArrowRight
                    size={14}
                    className="flex-shrink-0 mt-1 transition-transform group-hover:translate-x-0.5"
                    style={{ color: door.tint }}
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Welcome() {
  const navigate = useNavigate();
  // Already-unlocked testers shouldn't be asked for their invite code again.
  // Swap the CTAs to "Create account" / "Continue to dashboard" once we know
  // the gate has been passed (cookie present).
  const { unlocked: gateUnlocked } = useBetaGate();
  const { user } = useAuth();
  const isAuthenticated = !!(user && user.id);

  // Collapsible phase rows. Default: ALL phases collapsed — visitors first
  // see only the 5 phase circles. Clicking a circle opens that phase and
  // closes whichever phase was previously open (single-select). The header
  // chevron still lets users collapse the currently-open phase to return
  // to the clean "circles only" view.
  const [expandedPhases, setExpandedPhases] = useState(() => new Set());
  // Ref + one-shot observer for the bouncing guide arrow above the empty
  // prompt. We add the `--play` class the first time the empty prompt
  // enters the viewport, so the bounce fires when the visitor scrolls into
  // the section rather than the instant the page mounts (which they'd miss).
  const bounceArrowRef = useRef(null);
  useEffect(() => {
    const el = bounceArrowRef.current;
    if (!el) return undefined;
    // Honor reduced motion.
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    // Skip if the section is already deep above the fold — the user has
    // likely already scrolled past the cue.
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("rm-bounce-up--play");
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("rm-bounce-up--play");
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [expandedPhases.size]);
  const togglePhase = (id) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const openPhaseFromCircle = (id) => {
    // Single-select: replace whatever was open with just this phase. If the
    // user re-clicks the already-open circle, treat it as a "collapse" toggle
    // (so the circle row works like a real tab strip).
    setExpandedPhases((prev) => {
      if (prev.size === 1 && prev.has(id)) return new Set();
      return new Set([id]);
    });
    // Defer scroll past the layout shift caused by expanding the row.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="welcome-phase-detail-${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  // "Start here" suggestion — after the user has sat on an open phase for
  // >3s without picking a sub-tool, softly pulse the first sub-tile so the
  // natural entry point is obvious. Cancels on phase change, sub-tile click,
  // or unmount. Respects prefers-reduced-motion (the CSS rule no-ops).
  useEffect(() => {
    if (expandedPhases.size !== 1) return undefined;
    const activeId = [...expandedPhases][0];
    let pulseEl = null;
    let cleared = false;
    const onTileClick = () => {
      // Any sub-tile click clears the cue immediately — they're already on
      // their way to a tool.
      cleared = true;
      if (pulseEl) pulseEl.classList.remove("rm-suggest-pulse");
    };
    const timer = setTimeout(() => {
      if (cleared) return;
      pulseEl = document.querySelector(`[data-testid="welcome-phase-${activeId}-substep-1"]`);
      if (!pulseEl) return;
      // Restart the animation by removing+re-adding the class on the next
      // tick so re-opens replay the pulse.
      pulseEl.classList.remove("rm-suggest-pulse");
      void pulseEl.offsetWidth;
      pulseEl.classList.add("rm-suggest-pulse");
    }, 3000);
    const subStepContainer = document.querySelector(`[data-testid="welcome-phase-substeps-${activeId}"]`);
    if (subStepContainer) subStepContainer.addEventListener("click", onTileClick, { once: true });
    return () => {
      cleared = true;
      clearTimeout(timer);
      if (pulseEl) pulseEl.classList.remove("rm-suggest-pulse");
      if (subStepContainer) subStepContainer.removeEventListener("click", onTileClick);
    };
  }, [expandedPhases]);

  return (
    <div className="min-h-screen rm-paper" data-testid="welcome-page">
      <WelcomeTour />
      <NewsTicker />

      {/* Top bar with brand */}
      <header className="max-w-6xl mx-auto px-4 md:px-8 pt-4 md:pt-5 flex items-center justify-between">
        <div className="flex items-center gap-1 md:gap-2" data-testid="welcome-brand">
          <img
            src="/brand/resmo-welcoming.png"
            alt=""
            className="rm-shadow-bob"
            style={{ width: 160, height: 160, objectFit: "contain", marginRight: -12 }}
            draggable={false}
          />
          <div className="font-display leading-none"
               style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", letterSpacing: "-0.02em", color: "#0F1B2D" }}>
            <span className="rm-text-orange">R</span>esmo
            <span className="rm-text-orange">.</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" className="rm-btn rm-btn-ghost py-2 px-3 text-xs" data-testid="welcome-login-btn">
            <LogIn size={14} />
            <span className="hidden sm:inline">Returning user · Log in</span>
            <span className="sm:hidden">Log in</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pt-4 md:pt-6 pb-6">
        {/* TOP TAGLINE — spans full width across the page */}
        <div data-testid="welcome-tagline-block">
          <div className="font-mono-rm uppercase text-[11px] tracking-widest mb-2 rm-text-orange"
               data-testid="welcome-eyebrow">
            · Private beta · Career intelligence companion
          </div>
          <h1 className="font-display leading-none mb-3"
              style={{ fontSize: "clamp(1.9rem, 4.5vw, 3.2rem)", color: "#0F1B2D", letterSpacing: "-0.03em" }}
              data-testid="welcome-hero-title">
            One{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "#B91C1C",
              }}
            >
              resume
            </span>
            .{" "}
            <span className="rm-text-orange">Endless</span>{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #0E5A4A 0%, #1A7F5C 60%, #20C1A7 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "#0E5A4A",
              }}
            >
              opportunities
            </span>
            .
          </h1>
          <div
            className="font-display uppercase tracking-tight leading-tight mb-4"
            style={{
              fontSize: "clamp(0.95rem, 1.6vw, 1.35rem)",
              color: "#0F1B2D",
              letterSpacing: "-0.005em",
            }}
            data-testid="welcome-hero-subtagline"
          >
            <span style={{ color: "#FF6A00" }}>Be Discovered.</span>{" "}
            <span style={{ color: "#0E5A4A" }}>Be Understood.</span>{" "}
            <span style={{ color: "#0F1B2D" }}>Beyond the Resume.</span>
          </div>
          <p className="text-sm md:text-base leading-relaxed mb-5"
             style={{ color: "#334155", maxWidth: "62rem" }}
             data-testid="welcome-hero-tagline">
            Resmo is the career-intelligence companion that turns one base resume
            into tailored applications, salary insight, role research, and
            interview prep — built around <em>you</em>, not a job description.{" "}
            <strong style={{ color: "#0F1B2D" }}>A companion, at every step, to hold your hand.</strong>{" "}
            Whether you&apos;re looking for your first job or looking to transition into a
            new industry, Resmo has you covered.
          </p>

          <div className="flex flex-wrap gap-3" data-testid="welcome-cta-row">
            {isAuthenticated ? (
              <button
                onClick={() => navigate("/")}
                className="rm-btn rm-btn-primary py-3 px-5 text-sm"
                data-testid="welcome-dashboard-btn"
                style={{ boxShadow: "5px 5px 0 0 #0F1B2D" }}
              >
                <ArrowRight size={16} />
                Go to my dashboard
              </button>
            ) : gateUnlocked ? (
              <button
                onClick={() => navigate("/register")}
                className="rm-btn rm-btn-primary py-3 px-5 text-sm"
                data-testid="welcome-register-btn"
                style={{ boxShadow: "5px 5px 0 0 #0F1B2D" }}
              >
                <Sparkles size={16} />
                Create my account
              </button>
            ) : (
              <button
                onClick={() => navigate("/gate")}
                className="rm-btn rm-btn-primary py-3 px-5 text-sm"
                data-testid="welcome-invite-btn"
                style={{ boxShadow: "5px 5px 0 0 #0F1B2D" }}
              >
                <KeyRound size={16} />
                I have an invite code
              </button>
            )}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="rm-btn rm-btn-secondary py-3 px-5 text-sm"
                data-testid="welcome-login-cta"
              >
                <LogIn size={16} />
                I&apos;m a returning user
              </Link>
            )}
          </div>

          <div className="mt-3 text-xs flex items-center gap-2" style={{ color: "#475569" }}>
            <Shield size={12} />
            Private beta · NDA-protected · No public sign-ups yet.
          </div>
        </div>

        {/* ───── Title spans full width ───── */}
        <div className="mt-7 md:mt-9 text-center md:text-left" data-testid="welcome-video-title-block">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest rm-text-orange mb-1"
               style={{ fontWeight: 700 }}>
            · Resume magic · 2-minute reel
          </div>
          <h2
            className="font-display uppercase leading-tight"
            style={{
              fontSize: "clamp(1.4rem, 2.8vw, 2.2rem)",
              color: "#0F1B2D",
              letterSpacing: "-0.02em",
            }}
            data-testid="welcome-video-title"
          >
            Meet your new{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "#B91C1C",
              }}
            >
              best friend
            </span>
            , Resmo.
          </h2>
        </div>

        {/* ───── Video + Quick-Start picker (side-by-side, equal width + height) ─────
            Two-column 1:1 grid below the shared title. Both columns stretch to
            the same vertical height via `items-stretch` (CSS grid default) +
            `h-full` on the picker card. On mobile (`< md`), the grid collapses
            to single column — video first, picker stacks below. */}
        <div className="mt-3 md:mt-4 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-stretch"
             data-testid="welcome-video-picker-row">

          {/* LEFT — video stretches to match picker height */}
          <div className="flex" data-testid="welcome-video-col">
            <WelcomeIntro />
          </div>

          {/* RIGHT — compact dropdown-style Quick-Start picker, h-full */}
          <div className="flex" data-testid="welcome-picker-col">
            <QuickStartPicker isAuthenticated={isAuthenticated} gateUnlocked={gateUnlocked} />
          </div>
        </div>
      </section>

      {/* ─────────── Report catalog — shown once the user has a base resume ─────────── */}
      {user?.has_base_resume && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 pb-6" data-testid="welcome-reports-menu-wrap">
          <ReportsMenu />
        </section>
      )}


      {/* ─────────── Toolkit — 5-phase journey mirroring the dashboard ─────────── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="welcome-toolkit-section">
        <div
          className="rm-card-static relative overflow-hidden"
          style={{
            background: "white",
            borderColor: "#0F1B2D",
            boxShadow: "6px 6px 0 0 #FF6A00",
            padding: 0,
          }}
        >
          {/* ─── Top portion · NAVY · header + circle timeline ─── */}
          <div
            className="p-6 md:p-10"
            style={{
              background: "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 60%, #0F1B2D 100%)",
            }}
            data-testid="welcome-toolkit-header-band"
          >
            <div className="font-mono-rm uppercase text-[11px] tracking-widest mb-3"
                 style={{ color: "#FFC145", fontWeight: 700 }}>
              · What&apos;s in the toolkit · A companion at every step
            </div>
            <h2 className="font-display uppercase leading-none mb-4"
                style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)", color: "white", letterSpacing: "-0.02em" }}
                data-testid="welcome-toolkit-title">
              Five phases. <span style={{ color: "#FFC145" }}>One companion.</span>
            </h2>
            <p className="text-sm md:text-base max-w-3xl mb-6 leading-relaxed" style={{ color: "#E2E8F0" }}>
              The same 5-phase journey you&apos;ll see on your dashboard: Resume →
              Tailor → Network → Interview → Stepping Stones. Each phase
              unlocks a focused toolkit. Hover any statistic for its citation.
              <br />
              <span
                className="inline-block mt-2 font-mono-rm uppercase tracking-widest text-[11px]"
                style={{ color: "#FFC145", fontWeight: 700 }}
                data-testid="welcome-five-phases-hint"
              >
                · Select which phase you want to learn more about ↓
              </span>
            </p>

            {/* === Circle timeline — mirrors dashboard PhaseProgressBar === */}
            <div
              className="rm-card-static p-4 md:p-5 overflow-x-auto"
              style={{ background: "#FFFBEB", borderColor: "#FFC145", boxShadow: "3px 3px 0 0 #FFC145" }}
              data-testid="welcome-toolkit-timeline"
            >
            <div className="flex items-start justify-between gap-1 sm:gap-2 md:gap-3 min-w-[520px]">
              {WELCOME_PHASES.map((p, i) => {
                const palette = PHASE_GRADIENTS[p.id];
                const PhaseIcon = p.Icon;
                const showConnector = i < WELCOME_PHASES.length - 1;
                return (
                  <React.Fragment key={p.id}>
                    <div className="flex flex-col items-center flex-shrink-0"
                         style={{ width: 96, position: "relative" }}>
                      <button
                        type="button"
                        onClick={() => openPhaseFromCircle(p.id)}
                        className="relative flex items-center justify-center transition-transform hover:scale-105"
                        data-testid={`welcome-phase-circle-${p.id}`}
                        title={`Phase ${p.id} · ${p.label} — open this phase`}
                        aria-label={`Phase ${p.id} · ${p.label}. Opens this phase below.`}
                        aria-expanded={expandedPhases.has(p.id)}
                        aria-controls={`welcome-phase-body-${p.id}`}
                        style={{
                          width: 56, height: 56, borderRadius: "50%",
                          background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`,
                          color: "white",
                          border: "none",
                          cursor: "pointer",
                          // When the phase row below is open, draw an outer
                          // ring on the circle so the visual link circle↔row
                          // is obvious at a glance.
                          boxShadow: expandedPhases.has(p.id)
                            ? `0 0 0 3px #FFFBEB, 0 0 0 5px ${palette.solid}, 0 6px 14px ${palette.solid}55`
                            : `0 6px 14px ${palette.solid}55`,
                          transform: expandedPhases.has(p.id) ? "scale(1.06)" : "scale(1)",
                          transition: "transform 160ms ease-out, box-shadow 160ms ease-out",
                        }}
                      >
                        <PhaseIcon size={22} strokeWidth={2.2} />
                        {/* Top-right phase-number badge — matches dashboard style */}
                        <span
                          className="absolute inline-flex items-center justify-center"
                          style={{
                            top: -4, right: -4,
                            width: 22, height: 22, borderRadius: "50%",
                            background: "white", color: palette.solid,
                            border: `2px solid ${palette.solid}`,
                            boxShadow: "0 1px 2px rgba(15,27,45,0.18)",
                            fontSize: 11, fontWeight: 800, lineHeight: 1,
                          }}
                        >
                          {p.id}
                        </span>
                      </button>
                      <div
                        className="mt-2 text-[11px] uppercase tracking-wide font-semibold text-center leading-tight"
                        style={{ color: palette.solid, letterSpacing: "0.04em" }}
                      >
                        {p.label}
                      </div>
                    </div>
                    {showConnector && (
                      <div
                        className="flex-1 self-center -mt-3"
                        style={{ minWidth: 24 }}
                        aria-hidden
                      >
                        <div
                          style={{
                            height: 0,
                            borderTop: `2px dashed ${PHASE_GRADIENTS[p.id].solid}`,
                            opacity: 0.45,
                          }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          {/* ─── End of NAVY band ─── */}
          </div>

          {/* ─── Bottom portion · WHITE · Eat/Digest/Impress labels + phase detail cards ─── */}
          <div className="p-5 md:p-7" style={{ background: "white" }} data-testid="welcome-toolkit-body">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                   style={{ color: "#475569", fontWeight: 700 }}>
                · How Resmo works
              </div>
              {/* Expand-all / Collapse-all toggle. Single button — its label
                  flips based on whether all 5 phases are currently open. */}
              <button
                type="button"
                onClick={() => {
                  if (expandedPhases.size === WELCOME_PHASES.length) {
                    setExpandedPhases(new Set());
                  } else {
                    setExpandedPhases(new Set(WELCOME_PHASES.map((p) => p.id)));
                  }
                }}
                className="font-mono-rm uppercase text-[10px] tracking-widest px-2.5 py-1.5 rounded-md transition-colors"
                style={{
                  background: expandedPhases.size === WELCOME_PHASES.length ? "#0F1B2D" : "#FFC145",
                  color: expandedPhases.size === WELCOME_PHASES.length ? "#FFC145" : "#0F1B2D",
                  border: "1.5px solid #0F1B2D",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "2px 2px 0 0 #0F1B2D",
                }}
                aria-pressed={expandedPhases.size === WELCOME_PHASES.length}
                data-testid="welcome-phases-expand-toggle"
              >
                {expandedPhases.size === WELCOME_PHASES.length ? "Collapse all ↑" : "Expand all ↓"}
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-4 md:gap-5 items-stretch">
              {/* Left column — Eat / Digest / Impress pillar. Three shapes:
                  · 0 phases open → invisible spacer (reserves width)
                  · 1 phase open  → single dynamic pillar that echoes that
                                    phase's color
                  · 5 phases open → original 3-stacked layout (Eat=1, Digest=1,
                                    Impress=3) so each word centers next to
                                    its represented box(es). */}
              {(() => {
                if (expandedPhases.size === WELCOME_PHASES.length) {
                  // All-expanded · stacked Eat / Digest / Impress with the
                  // original flex proportions. Impress's `align=0.166` lands
                  // it at the vertical center of the Network card (the first
                  // of its 3-card span).
                  return (
                    <div
                      key="pillar-all"
                      className="md:w-[150px] md:flex-shrink-0 hidden md:flex flex-col gap-4 rm-pillar-swap"
                      data-testid="welcome-toolkit-pillars-rail"
                    >
                      <PillarBlock
                        testid="welcome-pillar-eat"
                        label="Eat"
                        textGradient="linear-gradient(180deg, #FCD34D 0%, #CA8A04 50%, #78350F 100%)"
                        fallbackColor="#CA8A04"
                        flex={1}
                      />
                      <PillarBlock
                        testid="welcome-pillar-digest"
                        label="Digest"
                        textGradient="linear-gradient(180deg, #F97316 0%, #C2410C 50%, #7C2D12 100%)"
                        fallbackColor="#C2410C"
                        flex={1}
                      />
                      <PillarBlock
                        testid="welcome-pillar-impress"
                        label="Impress"
                        textGradient="linear-gradient(180deg, #20C1A7 0%, #38BDF8 35%, #6366F1 70%, #7C3AED 100%)"
                        fallbackColor="#6366F1"
                        flex={3}
                        align={0.166}
                      />
                    </div>
                  );
                }
                const activeId = expandedPhases.size === 1 ? [...expandedPhases][0] : null;
                if (activeId == null) {
                  // Reserve the column width so the right-column box doesn't
                  // shift horizontally when a phase is picked / cleared.
                  return (
                    <div
                      className="md:w-[150px] md:flex-shrink-0 hidden md:block"
                      aria-hidden
                      data-testid="welcome-toolkit-pillar-empty"
                    />
                  );
                }
                let pillarProps;
                if (activeId === 1) {
                  pillarProps = {
                    label: "Eat",
                    testid: "welcome-pillar-eat",
                    textGradient: "linear-gradient(180deg, #FCD34D 0%, #CA8A04 50%, #78350F 100%)",
                    fallbackColor: "#CA8A04",
                  };
                } else if (activeId === 2) {
                  pillarProps = {
                    label: "Digest",
                    testid: "welcome-pillar-digest",
                    textGradient: "linear-gradient(180deg, #F97316 0%, #C2410C 50%, #7C2D12 100%)",
                    fallbackColor: "#C2410C",
                  };
                } else {
                  // Phases 3, 4, 5 all belong to the Impress span. Pick the
                  // gradient that matches the selected phase so the word
                  // visually echoes the row's palette (teal/blue/purple).
                  const impressGradient = activeId === 3
                    ? "linear-gradient(180deg, #5EEAD4 0%, #20C1A7 50%, #0F766E 100%)"
                    : activeId === 4
                      ? "linear-gradient(180deg, #93C5FD 0%, #3B82F6 50%, #1E3A8A 100%)"
                      : "linear-gradient(180deg, #C4B5FD 0%, #8B5CF6 50%, #5B21B6 100%)";
                  const impressFallback = activeId === 3 ? "#0F766E" : activeId === 4 ? "#1E3A8A" : "#5B21B6";
                  pillarProps = {
                    label: "Impress",
                    testid: "welcome-pillar-impress",
                    textGradient: impressGradient,
                    fallbackColor: impressFallback,
                  };
                }
                return (
                  <div
                    key={`pillar-${activeId}`}
                    className="md:w-[150px] md:flex-shrink-0 hidden md:flex rm-pillar-swap"
                    data-testid="welcome-toolkit-pillar-active"
                  >
                    <PillarBlock {...pillarProps} flex={1} />
                  </div>
                );
              })()}

              {/* Mobile-only horizontal pillar strip — single active word too. */}
              {expandedPhases.size === 1 && (() => {
                const activeId = [...expandedPhases][0];
                const word = activeId === 1 ? "Eat" : activeId === 2 ? "Digest" : "Impress";
                const color = activeId === 1 ? "#CA8A04"
                  : activeId === 2 ? "#C2410C"
                  : activeId === 3 ? "#0F766E"
                  : activeId === 4 ? "#1E3A8A"
                  : "#5B21B6";
                return (
                  <div
                    key={`pillar-mobile-${activeId}`}
                    className="flex md:hidden items-center justify-center mb-2 rm-pillar-swap"
                    data-testid="welcome-toolkit-pillars-rail-mobile"
                  >
                    <div className="rm-cursive text-3xl" style={{ color }}>{word}</div>
                  </div>
                );
              })()}

              {/* Right column — Phase detail cards. Only the currently
                  selected phase renders (single-select tabs pattern). When
                  nothing is selected we show a calm prompt so the section
                  doesn't feel empty. */}
              <div className="flex-1 space-y-4" data-testid="welcome-toolkit-phases">
            {expandedPhases.size === 0 && (
              <div
                className="rounded-md p-5 md:p-6 text-center relative"
                style={{
                  background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
                  border: "1.5px dashed #FFC145",
                }}
                data-testid="welcome-phase-empty-prompt"
              >
                {/* One-shot bouncing up-arrow — nudges the eye toward the
                    circles when the section first enters the viewport.
                    Lives inside the prompt so it disappears the moment the
                    user picks a phase (prompt unmounts). */}
                <div
                  ref={bounceArrowRef}
                  className="rm-bounce-up absolute left-1/2"
                  style={{
                    top: -28,
                    transform: "translateX(-50%)",
                    color: "#CA8A04",
                    filter: "drop-shadow(0 2px 6px rgba(202, 138, 4, 0.4))",
                  }}
                  aria-hidden="true"
                  data-testid="welcome-phase-empty-arrow"
                >
                  <ChevronUp size={34} strokeWidth={3} />
                </div>
                <div
                  className="font-mono-rm uppercase text-[10px] tracking-widest mb-1.5"
                  style={{ color: "#CA8A04", fontWeight: 700 }}
                >
                  · Tap a circle above
                </div>
                <div
                  className="font-display uppercase leading-tight"
                  style={{ fontSize: "1.1rem", color: "#0F1B2D", letterSpacing: "-0.01em" }}
                >
                  Pick a phase to see what&apos;s inside.
                </div>
                <div className="text-[12px] mt-2 leading-snug max-w-md mx-auto" style={{ color: "#475569" }}>
                  Each phase opens a focused toolkit — Resume, Tailor, Network,
                  Interview, or Stepping Stones. Click any circle to explore.
                </div>
              </div>
            )}
            {WELCOME_PHASES.map((p) => {
              const palette = PHASE_GRADIENTS[p.id];
              const PhaseIcon = p.Icon;
              const isOpen = expandedPhases.has(p.id);
              if (!isOpen) return null;
              return (
                <div
                  key={p.id}
                  className="rounded-md p-4 md:p-5"
                  style={{
                    background: palette.soft,
                    border: `1.5px solid ${palette.solid}40`,
                  }}
                  data-testid={`welcome-phase-detail-${p.id}`}
                >
                  {/* Header — clickable to toggle the body. Keep the entire
                      header row as the toggle target so users hit a generous
                      tap area on mobile. */}
                  <button
                    type="button"
                    onClick={() => togglePhase(p.id)}
                    className="w-full text-left flex items-start gap-3 flex-wrap"
                    style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                    aria-expanded={isOpen}
                    aria-controls={`welcome-phase-body-${p.id}`}
                    data-testid={`welcome-phase-header-${p.id}`}
                  >
                    {/* Mini phase chip on the left */}
                    <div
                      className="flex-shrink-0 flex items-center justify-center"
                      style={{
                        width: 44, height: 44, borderRadius: "50%",
                        background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`,
                        color: "white", boxShadow: `0 4px 10px ${palette.solid}50`,
                      }}
                      aria-hidden
                    >
                      <PhaseIcon size={20} strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                           style={{ color: palette.solid, fontWeight: 700 }}>
                        · Phase {p.id} · {p.tagline}
                      </div>
                      <div className="font-display uppercase leading-tight"
                           style={{ fontSize: "1.2rem", color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                        {p.label}
                      </div>
                    </div>
                    {p.stat && (
                      <div
                        className="font-mono-rm text-[10px] leading-snug italic cursor-help self-center"
                        style={{
                          color: palette.solid,
                          textDecoration: "underline",
                          textDecorationStyle: "dotted",
                          maxWidth: 260,
                          textAlign: "right",
                        }}
                        title={p.source ? `Source: ${p.source}` : undefined}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {p.stat}
                      </div>
                    )}
                    {/* Chevron — rotates 180° when open. Lives in its own
                        column so it always sits at the far right regardless
                        of whether the stat block is present. */}
                    <ChevronDown
                      size={18}
                      className="self-center flex-shrink-0"
                      style={{
                        color: palette.solid,
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 180ms ease-out",
                      }}
                      aria-hidden
                    />
                  </button>

                  {/* Collapsible body */}
                  {isOpen && (
                    <div
                      id={`welcome-phase-body-${p.id}`}
                      className="mt-3"
                      data-testid={`welcome-phase-body-${p.id}`}
                    >
                      {/* Sub-step tiles — what's INSIDE this phase (clickable shortcuts) */}
                      <div
                        className={`grid gap-2 ${
                          p.subSteps.length === 2 ? "sm:grid-cols-2"
                          : p.subSteps.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4"
                          : "sm:grid-cols-2 lg:grid-cols-3"
                        }`}
                        data-testid={`welcome-phase-substeps-${p.id}`}
                      >
                        {p.subSteps.map((sub, i) => {
                          const SubIcon = sub.Icon;
                          const destination = sub.route || "/";
                          // Unauthenticated visitors hitting a Protected route get
                          // bounced to /login by the router — we forward `next` so
                          // they land back on the right tool after auth.
                          const linkTarget = isAuthenticated
                            ? destination
                            : `/login?next=${encodeURIComponent(destination)}`;
                          return (
                            <Link
                              key={sub.route || sub.label || i}
                              to={linkTarget}
                              className="block p-3 rounded-md rm-substep-tile group focus:outline-none focus-visible:ring-2"
                              style={{
                                background: "white",
                                border: `2px solid ${palette.from}`,
                                transition: "transform 120ms ease-out, box-shadow 120ms ease-out, background 120ms ease-out",
                                textDecoration: "none",
                                color: "inherit",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translate(-2px, -2px)";
                                e.currentTarget.style.boxShadow = `4px 4px 0 0 ${palette.from}`;
                                e.currentTarget.style.background = palette.soft || "#FFF8EC";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "";
                                e.currentTarget.style.boxShadow = "";
                                e.currentTarget.style.background = "white";
                              }}
                              data-testid={`welcome-phase-${p.id}-substep-${i + 1}`}
                              aria-label={`${sub.label}: ${sub.sub}. Opens the ${sub.label} tool.`}
                            >
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2 min-w-0">
                                  <SubIcon size={16} style={{ color: palette.solid, flexShrink: 0 }} />
                                  <div className="font-display uppercase text-[13px] leading-tight truncate"
                                       style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                                    {sub.label}
                                  </div>
                                </div>
                                <ArrowRight
                                  size={13}
                                  className="opacity-40 group-hover:opacity-100"
                                  style={{
                                    color: palette.solid,
                                    flexShrink: 0,
                                    transition: "transform 120ms ease-out, opacity 120ms ease-out",
                                  }}
                                  aria-hidden
                                />
                              </div>
                              <div className="text-[12px] leading-snug" style={{ color: "#475569" }}>
                                {sub.sub}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
              </div> {/* /right column · welcome-toolkit-phases */}
            </div> {/* /flex row */}
          </div> {/* /welcome-toolkit-body (white) */}
        </div>
      </section>

      {/* ─────────── Verified by Resmo · NEW trust seal showcase ─────────── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="welcome-verified-section">
        <div
          className="rm-card-static p-6 md:p-10 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0F1B2D 0%, #16264A 55%, #0F1B2D 100%)",
            borderColor: "#0F1B2D",
            boxShadow: "6px 6px 0 0 #FFC145",
          }}
        >
          <div className="grid md:grid-cols-5 gap-6 md:gap-8 items-center">
            {/* LEFT — Copy */}
            <div className="md:col-span-3">
              <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
                   style={{ color: "#FFC145", fontWeight: 700 }}>
                · NEW in Resmo · Trust the tailored resume
              </div>
              <h2 className="font-display uppercase leading-tight mb-3"
                  style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)", color: "#FDFBF7", letterSpacing: "-0.02em" }}
                  data-testid="welcome-verified-title">
                The <span style={{
                  background: "linear-gradient(135deg, #FFC145 0%, #FF6A00 60%, #FFC145 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  color: "#FFC145",
                }}>&quot;Verified by Resmo&quot;</span> seal.
              </h2>
              <p className="text-sm md:text-base leading-relaxed mb-3" style={{ color: "#E2E8F0" }}>
                Every share link to a tailored resume now carries a live honesty score.
                Resmo audits the tailored version against your original base resume and
                flags every new claim — and the seal only appears <em>after you&apos;ve
                reviewed every flag</em>. Recruiters see a number they can trust at a glance.
              </p>
              <p className="text-sm leading-relaxed mb-4" style={{ color: "#FFC145", fontWeight: 600 }}>
                · No other resume tool offers this. It&apos;s a credibility layer
                that follows you from the share link to your LinkedIn — drop
                the embeddable seal anywhere you can paste HTML and it stays
                in sync as you re-review.
              </p>
              <div className="flex flex-wrap items-center gap-3" data-testid="welcome-verified-cta-row">
                {isAuthenticated ? (
                  <button
                    onClick={() => navigate("/history")}
                    className="rm-btn text-xs px-4 py-2"
                    style={{
                      background: "#FF6A00", color: "white",
                      border: "2px solid #0F1B2D",
                      boxShadow: "4px 4px 0 0 #0F1B2D",
                      fontWeight: 700,
                    }}
                    data-testid="welcome-verified-history-btn"
                  >
                    <ShieldCheck size={14} /> Verify one of mine →
                  </button>
                ) : (
                  <Link
                    to={gateUnlocked ? "/register" : "/gate"}
                    className="rm-btn text-xs px-4 py-2"
                    style={{
                      background: "#FF6A00", color: "white",
                      border: "2px solid #0F1B2D",
                      boxShadow: "4px 4px 0 0 #0F1B2D",
                      fontWeight: 700,
                    }}
                    data-testid="welcome-verified-register-btn"
                  >
                    <ShieldCheck size={14} /> Try Resmo with the Verified seal →
                  </Link>
                )}
                <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                      style={{ color: "#CBD5E1" }}>
                  · Free · Built into every tailored resume
                </span>
              </div>
            </div>

            {/* RIGHT — Visual preview of the seal */}
            <div className="md:col-span-2 flex justify-center md:justify-end" data-testid="welcome-verified-preview">
              <div className="flex flex-col items-center" data-testid="welcome-verified-seal-preview">
                <WelcomeSealPreview />
                <div className="mt-3 font-mono-rm text-[9px] uppercase tracking-widest text-center"
                     style={{ color: "#CBD5E1" }}>
                  · Sample seal · Real seals appear on /share links
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div
          className="rm-card-static p-6 md:p-8 text-center"
          style={{
            background: "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 100%)",
            borderColor: "#0F1B2D",
            boxShadow: "6px 6px 0 0 #FF6A00",
          }}
          data-testid="welcome-final-cta"
        >
          <Sparkles size={28} className="rm-text-orange mx-auto mb-3" />
          <h3 className="font-display uppercase leading-none mb-3"
              style={{ fontSize: "clamp(1.25rem, 2.4vw, 1.7rem)", color: "white", letterSpacing: "-0.02em" }}>
            Ready to take a bite?
          </h3>
          <p className="text-sm md:text-base mb-5 max-w-md mx-auto" style={{ color: "#CBD5E1" }}>
            We&apos;re in private beta. If you&apos;ve got an invite code, unlock now.
            Otherwise, we&apos;ll re-open soon.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {isAuthenticated ? (
              <button
                onClick={() => navigate("/")}
                className="rm-btn rm-btn-primary py-3 px-5 text-sm"
                data-testid="welcome-final-dashboard"
                style={{ boxShadow: "4px 4px 0 0 white" }}
              >
                <ArrowRight size={14} />
                Continue to dashboard
              </button>
            ) : gateUnlocked ? (
              <button
                onClick={() => navigate("/register")}
                className="rm-btn rm-btn-primary py-3 px-5 text-sm"
                data-testid="welcome-final-register"
                style={{ boxShadow: "4px 4px 0 0 white" }}
              >
                <Sparkles size={14} />
                Create my account
                <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={() => navigate("/gate")}
                className="rm-btn rm-btn-primary py-3 px-5 text-sm"
                data-testid="welcome-final-invite"
                style={{ boxShadow: "4px 4px 0 0 white" }}
              >
                <KeyRound size={16} />
                Enter invite code
                <ArrowRight size={14} />
              </button>
            )}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="rm-btn rm-btn-ghost py-3 px-5 text-sm"
                data-testid="welcome-final-login"
                style={{ color: "white", borderColor: "white" }}
              >
                Returning · Log in
              </Link>
            )}
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 md:px-8 py-6 text-center" data-testid="welcome-footer">
        <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
          Resmo · Career intelligence companion · © {new Date().getFullYear()}
        </div>
        <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-1.5">
          <Link
            to="/employer/signup"
            className="inline-flex items-center gap-1"
            style={{ color: "#FF6A00", fontWeight: 700, textDecoration: "none", letterSpacing: "0.08em" }}
            data-testid="welcome-footer-employer-link"
          >
            · For employers · Talent Directory →
          </Link>
        </div>
      </footer>
    </div>
  );
}

// (Legacy WhyResmoBlock + WHY_STATS removed — stats moved into the 7 toolkit-step tiles.)
